import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../services/api";
import { validateHashInput } from "../utils";
import { samplesAvailableSearchQueryKey } from "./queryKeys";
import { getUniqueHashes } from "../modules/alertsconfiguration/utils";
import { MAX_SELECT_HASHES } from "../modules/alertsconfiguration/const";
import { ISubscriptionItemSampleAvailable } from "../modules/alertsconfiguration/@types";

const useSampleAvailableSearch = (values: ISubscriptionItemSampleAvailable) => {
  const [searchValue, setSearchValue] = useState("");
  const currentHashValuesListLength = values?.hash_values?.length ?? 0;
  const { hash_values: currentHashValues = [] } = values ?? {};

  const { validHashes: validHashesFromSearch, invalidHashes } =
    validateHashInput(searchValue);

  const { data, isSuccess, isPending, isError, fetchStatus } = useQuery({
    queryKey: samplesAvailableSearchQueryKey(validHashesFromSearch),
    queryFn: () => api.availableForDownload(validHashesFromSearch),
    enabled: validHashesFromSearch.length > 0,
    refetchOnMount: false
  });

  const { availableSamples, nonAvailableSamples, isOverLimit } = useMemo(() => {
    const availableSamples = data?.data?.available ?? [];
    const privateSamples =
      validHashesFromSearch.filter(
        (hash: string) => !availableSamples.includes(hash)
      ) ?? [];

    const uniqueHashes = getUniqueHashes(privateSamples, currentHashValues);
    const privateSamplesListWithinMaxLimit = uniqueHashes.slice(
      0,
      MAX_SELECT_HASHES - currentHashValuesListLength
    );

    const inputIsOverHashLimit =
      uniqueHashes.length + currentHashValuesListLength > MAX_SELECT_HASHES;

    return {
      availableSamples: availableSamples,
      nonAvailableSamples: privateSamplesListWithinMaxLimit,
      isOverLimit: inputIsOverHashLimit
    };
  }, [data?.data?.available, validHashesFromSearch, currentHashValues]);

  return {
    setAvailableSamplesSearchValue: setSearchValue,
    availableSamples,
    nonAvailableSamples,
    invalidHashes,
    isOverLimit,
    isSuccess,
    isLoading: isPending,
    isError,
    fetchStatus
  };
};

export default useSampleAvailableSearch;
